/*!
 * Copyright © 2026. Verizon Connect Ireland Limited. All rights reserved.
 */

import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, select, Store } from '@ngrx/store';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, debounceTime, filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { PendoTrackEventService } from '../../../external/services';
import { FEATURE_TOGGLES } from '../../constants';
import { EPendoCustomEvents, IAttributeListItem, IAttributeListItemResponse } from '../../models';
import { delayedRetry } from '../../operators';
import { AttributeHttpService } from '../../services';
import {
  batchMappingRequests,
  buildEntityKey,
  buildMappingDiff,
  buildSearchPayload,
  convertEntityIdsToUuids as convertLegacyEntityIdsToUuids,
  fetchAllPages,
  flattenMappingResponses,
  MAX_ENTITY_IDS_PER_REQUEST,
  searchAllAssignments
} from '../../utils';
import {
  ClearAssignments,
  EAssetsActions,
  EAttributeActions,
  EMapPlotsActions,
  EnableFeatureToggle,
  EUserMapSettingsActions,
  GetAssetPlotsSuccess,
  GetUserMapSettingsSuccess,
  GetVehiclePlotsSuccess,
  LoadAttributes,
  LoadAttributesFailure,
  LoadAttributesSuccess,
  MapEntities,
  MapEntitiesFailure,
  RemoveSelectedAttributeValue,
  RetrySearchAssignments,
  SearchAssignments,
  SearchAssignmentsFailure,
  SearchAssignmentsSuccess,
  SetSelectedAttributes,
  UpdateEntityMappings
} from '../actions';
import {
  getAssetPlots,
  getAssignmentsError,
  getIsAttributeFilterEnabled,
  getLastSearchedEntityKey,
  getLegacyMapToUuid,
  getSelectedAttributes,
  getUuidMapToLegacy,
  getVehiclePlots
} from '../selectors';
import { IAppState } from '../state';

@Injectable()
export class AttributeEffects {
  public triggerLoadAttributes$: Observable<Action> = createEffect(() =>
    this._actions$.pipe(
      ofType<GetUserMapSettingsSuccess | EnableFeatureToggle>(
        EUserMapSettingsActions.GetUserMapSettingsSuccess,
        EUserMapSettingsActions.EnableFeatureToggle
      ),
      filter(action => {
        if (action instanceof EnableFeatureToggle) {
          return action.payload.includes(FEATURE_TOGGLES.AttributeFilter);
        }
        return true;
      }),
      withLatestFrom(this._store.pipe(select(getIsAttributeFilterEnabled))),
      filter(([, isEnabled]) => isEnabled),
      map(() => new LoadAttributes())
    )
  );

  public loadAttributes$: Observable<Action> = createEffect(() =>
    this._actions$.pipe(
      ofType<LoadAttributes>(EAttributeActions.LoadAttributes),
      switchMap(() =>
        fetchAllPages<IAttributeListItemResponse>(
          pagination =>
            this._attributeHttpService
              .getAttributes({
                limit: pagination.limit,
                offset: pagination.offset,
                totalRequired: pagination.totalRequired
              })
              .pipe(delayedRetry()),
          100
        ).pipe(
          map(items => items.map(item => this._mapToAttributeListItem(item))),
          map(items => new LoadAttributesSuccess(items)),
          catchError(() => of(new LoadAttributesFailure()))
        )
      )
    )
  );

  public searchAssignments$: Observable<Action> = createEffect(() =>
    this._actions$.pipe(
      ofType<SearchAssignments>(EAttributeActions.SearchAssignments),
      withLatestFrom(this._store.pipe(select(getLegacyMapToUuid)), this._store.pipe(select(getUuidMapToLegacy))),
      switchMap(([action, legacyToUuid, uuidToLegacy]) => {
        const uuidEntityIds = convertLegacyEntityIdsToUuids(action.payload.entityIds, legacyToUuid);
        return searchAllAssignments(
          body => this._attributeHttpService.searchAssignments(body).pipe(delayedRetry()),
          action.payload.attributeFilters,
          uuidEntityIds
        ).pipe(
          tap(items => {
            if (items.length === 0) {
              const attributesSelected = action.payload.attributeFilters.reduce((sum, f) => sum + f.stringValues.length, 0);
              this._pendoTrackEventService.sendPendoTrackEventWhenReady(EPendoCustomEvents.EmptyFilterResultsShown, {
                AttributesSelected: attributesSelected
              });
            }
          }),
          map(items =>
            items.map(item => {
              const compositeKey = uuidToLegacy[item.entityId];
              return compositeKey ? { ...item, entityId: compositeKey.substring(compositeKey.indexOf(':') + 1) } : item;
            })
          ),
          map(items => new SearchAssignmentsSuccess(items)),
          catchError(() => of(new SearchAssignmentsFailure()))
        );
      })
    )
  );

  public mapAndSearch$: Observable<Action> = createEffect(() =>
    this._actions$.pipe(
      ofType<MapEntities>(EAttributeActions.MapEntities),
      withLatestFrom(this._store.pipe(select(getLegacyMapToUuid))),
      switchMap(([action, existingMap]) => {
        const { attributeFilters, legacyEntityIds } = action.payload;
        const newIds = buildMappingDiff(legacyEntityIds, existingMap);

        if (newIds.length === 0) {
          return of(new SearchAssignments({ attributeFilters, entityIds: legacyEntityIds }));
        }

        const requests = batchMappingRequests(newIds, MAX_ENTITY_IDS_PER_REQUEST);
        return forkJoin(requests.map(req => this._attributeHttpService.mapToUuid(req).pipe(delayedRetry()))).pipe(
          switchMap(responses => {
            const newMappings = flattenMappingResponses(responses);
            return [new UpdateEntityMappings(newMappings), new SearchAssignments({ attributeFilters, entityIds: legacyEntityIds })];
          }),
          catchError(() => of(new MapEntitiesFailure()))
        );
      })
    )
  );

  public searchOnSelectedAttributesChange$: Observable<Action> = createEffect(() =>
    this._actions$.pipe(
      ofType<SetSelectedAttributes>(EAttributeActions.SetSelectedAttributes),
      withLatestFrom(this._store.pipe(select(getVehiclePlots)), this._store.pipe(select(getAssetPlots))),
      map(([action, vehiclePlots, assetPlots]) => {
        if (action.payload.length === 0) {
          return new ClearAssignments();
        }
        const { attributeFilters, entityIds } = buildSearchPayload(action.payload, vehiclePlots, assetPlots);
        return new MapEntities({ attributeFilters, legacyEntityIds: entityIds });
      })
    )
  );

  public clearAssignmentsOnLastChipRemoved$: Observable<Action> = createEffect(() =>
    this._actions$.pipe(
      ofType<RemoveSelectedAttributeValue>(EAttributeActions.RemoveSelectedAttributeValue),
      withLatestFrom(this._store.pipe(select(getSelectedAttributes))),
      filter(([, selected]) => selected.length === 0),
      map(() => new ClearAssignments())
    )
  );

  public retriggerSearchAssignmentsOnDatasetChange$: Observable<Action> = createEffect(() =>
    this._actions$.pipe(
      ofType<GetVehiclePlotsSuccess | GetAssetPlotsSuccess>(EMapPlotsActions.GetVehiclePlotsSuccess, EAssetsActions.GetAssetPlotsSuccess),
      debounceTime(300),
      withLatestFrom(
        this._store.pipe(select(getSelectedAttributes)),
        this._store.pipe(select(getVehiclePlots)),
        this._store.pipe(select(getAssetPlots)),
        this._store.pipe(select(getAssignmentsError)),
        this._store.pipe(select(getLastSearchedEntityKey))
      ),
      filter(([, selected, , , hasError]) => selected.length > 0 && !hasError),
      map(([, selected, vehiclePlots, assetPlots, , lastKey]) => ({
        payload: buildSearchPayload(selected, vehiclePlots, assetPlots),
        lastKey
      })),
      filter(({ payload, lastKey }) => buildEntityKey(payload.entityIds) !== lastKey),
      map(({ payload }) => new MapEntities({ attributeFilters: payload.attributeFilters, legacyEntityIds: payload.entityIds }))
    )
  );

  public retrySearchAssignments$: Observable<Action> = createEffect(() =>
    this._actions$.pipe(
      ofType<RetrySearchAssignments>(EAttributeActions.RetrySearchAssignments),
      withLatestFrom(
        this._store.pipe(select(getSelectedAttributes)),
        this._store.pipe(select(getVehiclePlots)),
        this._store.pipe(select(getAssetPlots))
      ),
      filter(([, selected]) => selected.length > 0),
      map(([, selected, vehiclePlots, assetPlots]) => {
        const { attributeFilters, entityIds } = buildSearchPayload(selected, vehiclePlots, assetPlots);
        return new MapEntities({ attributeFilters, legacyEntityIds: entityIds });
      })
    )
  );

  constructor(
    private readonly _actions$: Actions,
    private readonly _attributeHttpService: AttributeHttpService,
    private readonly _store: Store<IAppState>,
    private readonly _pendoTrackEventService: PendoTrackEventService
  ) {}

  private _mapToAttributeListItem(item: IAttributeListItemResponse): IAttributeListItem {
    return {
      attributeId: item.attributeId,
      name: item.name,
      description: item.description,
      stringValues: item.stringValues
    };
  }
}
